import { Avatar, Button, Flex, Modal, Typography } from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { MixpanelClient } from '~/plugins/mixpanel/client'
import { AgentButton } from './components/AgentButton'
import { AgentChatInput } from './components/AgentChatInput'
import { useTextSelection } from './hooks/useTextSelection'
import { useToolbarPosition } from './hooks/useToolbarPosition'

type Props = {
  agent?: OrganizationMemberFromContext
  containerRef: React.RefObject<HTMLDivElement>
  onAskAI?: (selectedText: string, guidelines?: string) => void
  isUpdating?: boolean
}

export const AgentAssistant: React.FC<Props> = ({
  agent,
  containerRef,
  onAskAI,
  isUpdating = false,
}) => {
  const mixpanel = MixpanelClient.use()

  const designSystem = DesignSystem.use()

  const inputRef = useRef<HTMLDivElement>(null)
  const [isAgentInputVisible, setAgentInputVisible] = useState(false)

  const [hasSeenHint, setHasSeenHint] = useState(() => {
    return localStorage.getItem('has_seen_ai_edit_hint') === 'true'
  })

  const {
    selectedText,
    selectionRects,
    isAskButtonVisible,
    setAskButtonVisible,
    setSelectedText,
    setSelectionRects,
    lastSelectionRef,
  } = useTextSelection(inputRef)

  const toolbarStyle = useToolbarPosition(containerRef)

  useEffect(() => {
    const hasText = selectedText && selectedText.trim().length > 0
    if (hasText && !isAgentInputVisible) {
      setAskButtonVisible(true)
    } else {
      setAskButtonVisible(false)
    }
  }, [selectedText, isAgentInputVisible])

  useEffect(() => {
    if (agent && selectedText) {
      requestAnimationFrame(() => {
        const selection = window.getSelection()
        const range = lastSelectionRef.current
        if (selection && range instanceof Range) {
          selection.removeAllRanges()
          selection.addRange(range)
        }
      })
    }
  }, [agent, selectedText])

  useEffect(() => {
    if (!isUpdating) {
      setAgentInputVisible(false)
    }
  }, [agent, selectedText, isUpdating])

  const handleSubmit = (guidelines: string) => {
    onAskAI?.(selectedText, guidelines)
    setSelectedText('')
    setSelectionRects([])
    setAskButtonVisible(false)
  }

  return (
    <>
      {!hasSeenHint && !designSystem.isMobile && (
        <Modal
          mask={false}
          open={!hasSeenHint}
          closable={false}
          footer={
            <Button
              type="default"
              onClick={() => {
                localStorage.setItem('has_seen_ai_edit_hint', 'true')
                mixpanel.track('blogWriter.agent.assistant.accepted')
                setHasSeenHint(true)
              }}
            >
              Got it
            </Button>
          }
          centered
        >
          <Flex gap={8} align="center">
            <Avatar
              size={36}
              src={agent?.appSession.pictureUrl}
              style={{ flexShrink: 0, border: '1px solid grey' }}
            />
            <Typography.Text>
              Try selecting any text in the article — I can help you rewrite or
              improve it!
            </Typography.Text>
          </Flex>
        </Modal>
      )}
      <AnimatePresence>
        {isAgentInputVisible && agent && (
          <motion.div
            ref={inputRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ ...toolbarStyle, zIndex: 1 }}
            onAnimationComplete={() => {
              const senderEl = inputRef.current?.querySelector('textarea')
              senderEl?.focus()
            }}
          >
            <AgentChatInput
              agent={agent}
              onSubmit={handleSubmit}
              isUpdating={isUpdating}
              selectionType="text"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {isAskButtonVisible &&
        !isAgentInputVisible &&
        selectionRects.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              left: Math.min(
                selectionRects[0].left -
                  containerRef.current!.getBoundingClientRect().left,
                containerRef.current!.offsetWidth - 150,
              ),
              top: designSystem.isMobile
                ? selectionRects[0].bottom -
                  containerRef.current!.getBoundingClientRect().top +
                  10
                : selectionRects[0].top -
                  containerRef.current!.getBoundingClientRect().top -
                  60,
            }}
          >
            <AgentButton
              agent={agent}
              onClick={() => {
                mixpanel.track('blogWriter.agent.assistant.button.clicked')
                setAgentInputVisible(true)
              }}
            />
          </motion.div>
        )}

      {isAgentInputVisible &&
        selectionRects.map((rect, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top:
                rect.top -
                containerRef.current!.getBoundingClientRect().top -
                5,
              left:
                rect.left - containerRef.current!.getBoundingClientRect().left,
              width: rect.width,
              height: rect.height + 10,
              background: 'rgba(100, 150, 255, 0.4)',
              pointerEvents: 'none',
            }}
          />
        ))}
    </>
  )
}
